import type { PointerEvent } from 'react';
import { useRef, useCallback } from 'react';
import { distance } from '@/utils/distance';

export const useClickHandler = (
  handleClick: () => void,
  { pixelThreshold = 3, clickDuration = 300 } = {}
): { onPointerDown: (e: PointerEvent) => void; onPointerUp: (e: PointerEvent) => void } => {
  const clickInfo = useRef({ isDown: true, timestamp: 0, point: { x: 0, y: 0 } });

  const onPointerDown = useCallback((e: PointerEvent) => {
    e.preventDefault();
    clickInfo.current.isDown = true;
    clickInfo.current.point = { x: e.clientX, y: e.clientY };
    clickInfo.current.timestamp = Date.now();
  }, []);

  const onPointerUp = useCallback(
    (e: PointerEvent) => {
      e.preventDefault();
      if (clickInfo.current.isDown) {
        clickInfo.current.isDown = false;
        const dist = distance({ x: e.clientX, y: e.clientY }, clickInfo.current.point);
        const duration = Date.now() - clickInfo.current.timestamp;

        // only triggering click when pointer hasn't moved (user isn't dragging) and duration is within certain threshold
        if (dist < pixelThreshold && duration < clickDuration) {
          handleClick();
        }
      }
    },
    [handleClick, pixelThreshold, clickDuration]
  );

  return { onPointerDown, onPointerUp };
};
